import i18n from '@/i18n';
import { useToastStore } from '@/store/useToastStore';
import { cs, en, pl, ro, sk } from 'vuetify/locale';
import { EshopI18NController, I18nMessageResponse } from '@/api';
import { useLocaleStore } from '@/store/useLocaleStore';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore';

const vuetifyLocale = { en, sk, cs, pl, ro };

type MessageSection = {
  [key: string]: string;
};

type MessageSections = {
  [sectionKey: string]: MessageSection;
};

type MessageTree = {
  [key: string]: string | string[] | MessageTree;
};

type I18nMessages = MessageTree & { dynamic: MessageSections };

export const t = (key: string, value = {}) => {
  return i18n.global.t(key, value);
};

export const te = (key: string) => {
  return i18n.global.te(key);
};

export class KeyGroupI18n {
  private readonly keyGroup: string;
  private readonly options: { ignoreNonExisting: boolean };

  constructor(keyGroup: string, options?: { ignoreNonExisting: boolean }) {
    this.keyGroup = keyGroup;
    this.options = options ?? { ignoreNonExisting: false };
  }

  te = (key: string) => {
    const composedKey = this.getComposedKey(key);
    return te(composedKey) ?? te(key);
  };
  getComposedKey = (key: string) => {
    return this.keyGroup ? `${this.keyGroup}.${key}` : key;
  };

  t = (key: string, value = {}) => {
    const composedKey = this.getComposedKey(key);
    if (!te(composedKey)) {
      if (this.options.ignoreNonExisting) {
        return te(key) ? t(key, value) : '';
      } else {
        // translate the key without its group
        return t(key, value);
      }
    }
    return t(composedKey, value);
  };
}

const setLocale = (locale: string, messages: I18nMessages): void => {
  i18n.global.setLocaleMessage(locale, {
    ...messages,
  });
  i18n.global.locale.value = locale;

  const html = document.documentElement;
  html.setAttribute('lang', i18n.global.locale.value);

  document.title = i18n.global.t('__tenantTitle');
};

export async function loadLocaleMessages(): Promise<void> {
  const localeStore = useLocaleStore();
  const toastStore = useToastStore();

  try {
    const preferredLocale = localeStore.locale;
    const response = await EshopI18NController.getMessages({
      headers: { 'Accept-Language': preferredLocale },
    });

    if (!response.response.ok) {
      throw new Error('Cannot be processed with locales because the server does not provide the correct response.');
    }

    const resolvedLocale = response.response.headers.get('content-language') ?? preferredLocale;
    const messages = (response.data as I18nMessageResponse).data;

    setLocale(resolvedLocale, {
      ...messages,
      dynamic: messages?.dynamic ?? {},
      $vuetify: vuetifyLocale[resolvedLocale],
    });
  } catch (e) {
    toastStore.error({
      message: 'An error occurred while trying to load translated messages.',
      options: {
        timeout: -1,
        showClose: false,
        x: 'center',
        y: 'top',
      },
    });
    throw e;
  }
}

export async function loadSupportedLocales(): Promise<void> {
  const localeStore = useLocaleStore();
  const toastStore = useToastStore();

  try {
    const response = await EshopI18NController.getSupportedLocales();
    localeStore.setSupportedLocales(response.data?.data ?? []);
  } catch (e) {
    toastStore.error({
      message: 'An error occurred while trying to load supported locales.',
      options: {
        timeout: -1,
        showClose: false,
        x: 'center',
        y: 'top',
      },
    });
    throw e;
  }
}

enum TranslationSectionNames {
  tariffProfile = 'TARIFF_PROFILE',
  customerProfile = 'CUSTOMER_PROFILE',
  ticketCombination = 'TICKET_COMBINATION',
  fee = 'FEE',
  zone = 'ZONE',
}

export const enum InvalidFieldNames {
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
  phone = 'phone',
  street = 'street',
  streetNumber = 'streetNumber',
  city = 'city',
  zip = 'zip',
  profile = 'profile',
  isic = 'isic',
  note = 'note',
  photo = 'photo',
  deliveryType = 'deliveryType',
  bankCard = 'bankCard',
  branchOffice = 'branchOffice',
  ticketCombination = 'ticketCombination',
  validFrom = 'validFrom',
  validTo = 'validTo',
  zone = 'zone',
  expiration = 'expiration',
  price = 'price',
  snr = 'snr',
}

const getDynamicTranslation = (section: TranslationSectionNames, id: string): string => t(`dynamic.${section}.${id}`);

export const getCustomerProfileDisplayName = (id: string | undefined): string =>
  id ? getDynamicTranslation(TranslationSectionNames.customerProfile, id) : '';

export const getTariffProfileDisplayName = (id: string | undefined): string =>
  id ? getDynamicTranslation(TranslationSectionNames.tariffProfile, id) : '';

export const getTicketCombinationDisplayName = (id: string | undefined): string =>
  id ? getDynamicTranslation(TranslationSectionNames.ticketCombination, id) : '';

export const getFeeDisplayName = (id: string | undefined): string =>
  id ? getDynamicTranslation(TranslationSectionNames.fee, id) : '';

export const getZoneDisplayName = (id: string | undefined): string =>
  id ? getDynamicTranslation(TranslationSectionNames.zone, id) : '';

export const getCurrencyString = (): string => {
  const systemConfigurationStore = useSystemConfigurationStore();
  const currency = systemConfigurationStore.currency?.Code;
  if (!currency) {
    return i18n.global.t('currency.CZK');
  }

  return i18n.global.t(`currency.${currency}`);
};
